import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Message, Vide } from "@cabinet/charte";
import {
  achatsApi,
  dossiersApi,
  ecrituresApi,
  ErreurApi,
  fournisseursApi,
  type AlerteDeDoublonVue,
  type SaisieDeFacture,
} from "../api/client";
import {
  ecartAvecLeTotalAnnonce,
  estPrete,
  lignesAEnvoyer,
  ligneVide,
  montantIllisible,
  totauxDeFacture,
  type LigneDeFacture,
} from "../domaine/facture";
import { formaterDate, formaterMontant } from "../domaine/formats";
import { useSession } from "../session";

const aujourdHui = () => new Date().toISOString().slice(0, 10);

/**
 * Saisie d'une facture d'achat (EF-0502, EF-0504).
 *
 * On ventile des bases hors taxes par compte et par taux ; la taxe s'impute là
 * où le référentiel l'envoie — le 34552 pour une charge, le 34551 pour une
 * immobilisation —, et le fournisseur est crédité du toutes taxes, à
 * l'échéance de ses conditions de règlement.
 *
 * Les totaux s'affichent pendant la frappe, calculés localement en centimes ;
 * la ventilation qui fait foi est celle du serveur, demandée dès que la
 * saisie tient debout. C'est le même appel qui cherche les doublons, avant
 * que rien ne soit écrit.
 */
export function PageFactureDAchat() {
  const { id = "" } = useParams();
  const navigue = useNavigate();
  const cache = useQueryClient();
  const { session } = useSession();

  const [fournisseur, setFournisseur] = useState("");
  const [numero, setNumero] = useState("");
  const [date, setDate] = useState(aujourdHui);
  const [libelle, setLibelle] = useState("");
  const [echeance, setEcheance] = useState("");
  const [totalAnnonce, setTotalAnnonce] = useState("");
  const [lignes, setLignes] = useState<LigneDeFacture[]>([ligneVide(), ligneVide()]);
  // Le doublon s'assume pour *une* facture précise : la décision est
  // retenue avec la facture qu'elle visait, et cesse de valoir dès que le
  // fournisseur, le numéro ou la date changent — sans effet de bord.
  const [doublonAssumePour, setDoublonAssumePour] = useState<string | null>(null);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const fournisseurs = useQuery({
    queryKey: ["fournisseurs", id, "", false],
    queryFn: () => fournisseursApi.lister(id),
  });
  const bareme = useQuery({
    queryKey: ["bareme", id, date],
    queryFn: () => ecrituresApi.bareme(id, date),
    enabled: date !== "",
  });
  const taux = useMemo(
    () => (bareme.data?.taux ?? []).map((t) => ({ code: t.code, pourCent: t.pourCent })),
    [bareme.data],
  );
  const totaux = totauxDeFacture(lignes, taux);
  const ecart = ecartAvecLeTotalAnnonce(totaux, totalAnnonce);
  const prete = estPrete({ fournisseur, numero, date }, lignes);
  const fiche = fournisseurs.data?.find((f) => f.code === fournisseur);
  const cleDeLaFacture = `${fournisseur}|${numero.trim()}|${date}`;
  const doublonAssume = doublonAssumePour === cleDeLaFacture;

  const saisie: SaisieDeFacture = {
    fournisseur,
    numero: numero.trim(),
    date,
    lignes: lignesAEnvoyer(lignes),
    libelle: libelle.trim() === "" ? null : libelle.trim(),
    echeance: echeance === "" ? null : echeance,
    journal: null,
    totalTtcAttendu: totalAnnonce.trim() === "" ? null : Number(totalAnnonce.replace(",", ".")),
  };

  // Le contrôle se demande dès que la saisie tient debout : il ventile, dit
  // l'échéance retenue, et cherche les doublons — le tout sans rien écrire.
  const controle = useQuery({
    queryKey: ["facture-controle", id, JSON.stringify(saisie)],
    queryFn: () => achatsApi.controler(id, saisie),
    enabled: prete,
    retry: false,
  });

  const enregistrement = useMutation({
    mutationFn: (options: { valider: boolean }) =>
      achatsApi.enregistrer(id, saisie, { doublonAssume, valider: options.valider }),
    onSuccess: (resultat) => {
      void cache.invalidateQueries({ queryKey: ["pieces"] });
      void cache.invalidateQueries({ queryKey: ["echeancier", id] });
      navigue(`/dossiers/${id}/ecritures?piece=${resultat.piece.id}`);
    },
  });

  const peutValider = session?.permissions.includes("VALIDER_ECRITURE") ?? false;
  const alertes = controle.data?.alertes ?? [];
  const doublonCertain = controle.data?.doublonCertain ?? false;

  function modifierLigne(rang: number, champ: keyof LigneDeFacture, valeur: string) {
    setLignes((precedentes) => {
      const suivantes = precedentes.map((ligne, index) =>
        index === rang ? { ...ligne, [champ]: valeur } : ligne,
      );
      // Une dernière ligne servie en ouvre une nouvelle : la ventilation
      // n'oblige jamais à cliquer pour continuer.
      const derniere = suivantes[suivantes.length - 1];
      if (derniere.compte.trim() !== "" || derniere.baseHT.trim() !== "") suivantes.push(ligneVide());
      return suivantes;
    });
  }

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Facture d'achat"
        description="Les bases hors taxes se ventilent par compte et par taux ; la TVA s'impute là où le référentiel l'envoie, et le fournisseur est crédité du toutes taxes à l'échéance de ses conditions de règlement."
        actions={
          <Link to={`/dossiers/${id}/fournisseurs`} className="secondaire">
            Fournisseurs
          </Link>
        }
      />

      {dossier.error != null && (
        <Message type="erreur" titre={dossier.error instanceof ErreurApi ? dossier.error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{dossier.error.message}</p>
        </Message>
      )}

      {fournisseurs.data != null && fournisseurs.data.length === 0 && (
        <Vide>
          Aucune fiche fournisseur sur ce dossier. <Link to={`/dossiers/${id}/fournisseurs`}>En ouvrir une</Link>{" "}
          : c'est elle qui porte le code d'auxiliaire et les conditions de règlement.
        </Vide>
      )}

      <form className="saisie facture-achat" onSubmit={(e) => e.preventDefault()}>
        <div className="filtres-plan">
          <select value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} aria-label="Fournisseur">
            <option value="">— Fournisseur —</option>
            {fournisseurs.data?.map((f) => (
              <option key={f.id} value={f.code}>
                {f.code} — {f.raisonSociale}
              </option>
            ))}
          </select>
          <input
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            placeholder="N° de la facture"
            aria-label="Numéro de la facture"
          />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date de facture" />
          <input
            type="date"
            value={echeance}
            onChange={(e) => setEcheance(e.target.value)}
            aria-label="Échéance"
            title="Échéance ; à défaut, celle des conditions de règlement du fournisseur"
          />
          <input
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder="Libellé de la pièce"
            aria-label="Libellé"
          />
          <input
            value={totalAnnonce}
            onChange={(e) => setTotalAnnonce(e.target.value)}
            placeholder="Total TTC annoncé"
            aria-label="Total toutes taxes annoncé par la facture"
            className="nombre"
          />
        </div>

        {fiche != null && (
          <p className="aide-cellule">
            {fiche.raisonSociale} · {fiche.conditions.resume}
            {fiche.conditions.moyenLibelle != null && ` · ${fiche.conditions.moyenLibelle}`} · compte{" "}
            {fiche.compteDeRattachement}
            {fiche.identifiantPrincipal == null && (
              <>
                {" · "}
                <Etiquette variante="avertissement">
                  Sans ICE : les conditions de forme du droit à déduction ne sont pas réunies
                </Etiquette>
              </>
            )}
          </p>
        )}

        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Compte</th>
                <th>Libellé</th>
                <th className="nombre">Base HT</th>
                <th>Taux</th>
                <th className="nombre">TVA de la facture</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((ligne, rang) => (
                <tr key={rang}>
                  <td>
                    <input
                      value={ligne.compte}
                      onChange={(e) => modifierLigne(rang, "compte", e.target.value)}
                      placeholder="611"
                      aria-label={`Compte de la ligne ${rang + 1}`}
                    />
                  </td>
                  <td>
                    <input
                      value={ligne.libelle}
                      onChange={(e) => modifierLigne(rang, "libelle", e.target.value)}
                      placeholder="Libellé du compte"
                      aria-label={`Libellé de la ligne ${rang + 1}`}
                    />
                  </td>
                  <td className="nombre">
                    <input
                      value={ligne.baseHT}
                      onChange={(e) => modifierLigne(rang, "baseHT", e.target.value)}
                      className={montantIllisible(ligne.baseHT) ? "nombre erreur" : "nombre"}
                      aria-label={`Base hors taxes de la ligne ${rang + 1}`}
                    />
                  </td>
                  <td>
                    <select
                      value={ligne.codeDeTva}
                      onChange={(e) => modifierLigne(rang, "codeDeTva", e.target.value)}
                      aria-label={`Taux de la ligne ${rang + 1}`}
                    >
                      <option value="">Hors champ</option>
                      {bareme.data?.taux.map((t) => (
                        <option key={t.code} value={t.code}>
                          {t.libelle}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="nombre">
                    <input
                      value={ligne.tva}
                      onChange={(e) => modifierLigne(rang, "tva", e.target.value)}
                      placeholder="calculée"
                      disabled={ligne.codeDeTva === ""}
                      className="nombre"
                      aria-label={`TVA portée par la facture, ligne ${rang + 1}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total">
                <td colSpan={2}>Ventilation</td>
                <td className="nombre">{formaterMontant(totaux.totalHT)}</td>
                <td className="discret-texte">TVA</td>
                <td className="nombre">{formaterMontant(totaux.totalTva)}</td>
              </tr>
              <tr className="total">
                <td colSpan={4}>Total toutes taxes</td>
                <td className="nombre">{formaterMontant(totaux.totalTTC)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {totaux.tauxInconnu && (
          <Message type="avertissement" titre="Taux inconnu à cette date">
            <p style={{ margin: "6px 0 0" }}>
              Un taux retenu n'est pas en vigueur le {formaterDate(date)}. Le barème est daté : la valeur
              appliquée est celle du jour de la facture, jamais la valeur courante.
            </p>
          </Message>
        )}

        {ecart !== 0 && (
          <Message type="avertissement" titre="Écart avec le total annoncé">
            <p style={{ margin: "6px 0 0" }}>
              La facture annonce {formaterMontant(Number(totalAnnonce.replace(",", ".")))} et la ventilation
              donne {formaterMontant(totaux.totalTTC)}, soit {formaterMontant(Math.abs(ecart))} d'écart.
            </p>
          </Message>
        )}

        {controle.data != null && (
          <p className="aide-cellule">
            Échéance retenue : {formaterDate(controle.data.facture.echeance)} · TVA{" "}
            {formaterMontant(controle.data.facture.totalTva)} sur{" "}
            {controle.data.facture.lignes
              .filter((l) => l.compteDeTva != null)
              .map((l) => l.compteDeTva)
              .filter((compte, index, tous) => tous.indexOf(compte) === index)
              .join(", ") || "aucun compte"}
          </p>
        )}

        {alertes.length > 0 && <Doublons alertes={alertes} />}

        {controle.error != null && (
          <Message
            type="erreur"
            titre={controle.error instanceof ErreurApi ? controle.error.titre : "Contrôle impossible"}
          >
            <p style={{ margin: "6px 0 0" }}>{controle.error.message}</p>
          </Message>
        )}

        {enregistrement.error != null && (
          <Message
            type="erreur"
            titre={enregistrement.error instanceof ErreurApi ? enregistrement.error.titre : "Erreur"}
          >
            <p style={{ margin: "6px 0 0" }}>{enregistrement.error.message}</p>
          </Message>
        )}

        <div className="barre-actions">
          {doublonCertain && (
            <label className="discret-texte">
              <input
                type="checkbox"
                checked={doublonAssume}
                onChange={(e) => setDoublonAssumePour(e.target.checked ? cleDeLaFacture : null)}
              />{" "}
              Enregistrer malgré le doublon — la décision est consignée
            </label>
          )}
          <button
            type="button"
            onClick={() => enregistrement.mutate({ valider: false })}
            disabled={!prete || enregistrement.isPending || (doublonCertain && !doublonAssume)}
          >
            Enregistrer en brouillard
          </button>
          {peutValider && (
            <button
              type="button"
              className="secondaire"
              onClick={() => enregistrement.mutate({ valider: true })}
              disabled={!prete || enregistrement.isPending || (doublonCertain && !doublonAssume)}
            >
              Enregistrer et valider
            </button>
          )}
          {controle.isFetching && <span className="discret-texte">Contrôle en cours…</span>}
        </div>
      </form>

      {dossier.isPending && <Chargement quoi="le dossier" />}
    </>
  );
}

/* ------------------------------------------------------------------ */

/** Ce que la recherche de doublons a trouvé, du plus certain au moins certain (EF-0504). */
function Doublons({ alertes }: { alertes: AlerteDeDoublonVue[] }) {
  const certain = alertes.some((alerte) => alerte.certitude === "CERTAIN");
  return (
    <Message
      type={certain ? "erreur" : "avertissement"}
      titre={certain ? "Cette facture semble déjà enregistrée" : "Une facture semblable existe"}
    >
      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        {alertes.map((alerte) => (
          <li key={`${alerte.piece}-${alerte.certitude}`}>
            {alerte.motif}{" "}
            <span className="discret-texte">
              ({alerte.statut === "VALIDEE" ? "validée" : "brouillard"},{" "}
              {formaterMontant(alerte.montantTtc)})
            </span>
          </li>
        ))}
      </ul>
    </Message>
  );
}
